import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Gift, X } from "lucide-react";
import { getPendingRef } from "../lib/referral";

// Floating banner that nudges visitors who landed via ?ref=XXXXXX to claim
// their free month. Hides itself once the user goes to /settings (where they
// can actually redeem) or once they dismiss it.
const DISMISS_KEY = "fff_ref_banner_dismissed";

const RefBanner = () => {
  const [code, setCode] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Read URL first (so initial landing renders the banner without waiting for
    // App's captureRefFromUrl effect), then fall back to localStorage.
    let found = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = (params.get("ref") || "").trim().toUpperCase();
      if (fromUrl && /^[A-Z0-9]{4,10}$/.test(fromUrl)) found = fromUrl;
    } catch { /* ignore */ }
    if (!found) found = getPendingRef();
    setCode(found);
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === "1"); } catch { /* ignore */ }
  }, [location.pathname, location.search]);

  // Hide on the settings page itself (redeem UI is already visible there)
  if (!code || dismissed || location.pathname.startsWith("/settings")) return null;

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="ref-banner"
      className="fixed top-2 left-2 right-2 z-[10005] sm:left-auto sm:right-3 sm:top-3 sm:max-w-sm rounded-2xl border border-[#BEFF38]/40 bg-gradient-to-br from-[#1d2606] to-[#0b0b0c] shadow-lg shadow-[#BEFF38]/10 px-3 py-2.5 flex items-center gap-3"
    >
      <div className="h-8 w-8 rounded-full bg-[#BEFF38]/20 grid place-items-center shrink-0">
        <Gift className="h-4 w-4 text-[#BEFF38]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-[13px] font-bold leading-tight">A friend gave you 30 days free</p>
        <p className="text-zinc-400 text-[11px]">Code <span className="font-mono text-[#BEFF38] tracking-wider">{code}</span> ready to redeem</p>
      </div>
      <button
        onClick={() => navigate("/settings")}
        data-testid="ref-banner-claim"
        className="shrink-0 px-3 h-9 rounded-full bg-[#BEFF38] hover:bg-lime-300 text-black text-[11px] font-black uppercase tracking-wider transition-colors"
      >
        Claim
      </button>
      <button
        onClick={onDismiss}
        data-testid="ref-banner-close"
        aria-label="Dismiss"
        className="shrink-0 h-7 w-7 rounded-full hover:bg-white/10 grid place-items-center text-zinc-400"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default RefBanner;
